import { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Search,
  Filter,
  MoreVertical,
  Eye,
  Edit,
  Trash2,
  CheckCircle,
  Link2,
  ChevronRight,
  ChevronDown,
  FileText,
  AlertTriangle,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import type {
  Requirement,
  RequirementFilters,
  RequirementCategory,
  RequirementLevel,
  RequirementStatus,
  RequirementPriority,
} from '@/types/requirements';
import {
  categoryLabels,
  levelLabels,
  statusLabels,
  priorityLabels,
  categoryColors,
  statusColors,
  priorityColors,
  levelColors,
} from '@/types/requirements';

interface RequirementsTableProps {
  requirements: Requirement[];
  isLoading?: boolean;
  filters: RequirementFilters;
  onFiltersChange: (filters: RequirementFilters) => void;
  onView: (requirement: Requirement) => void;
  onEdit: (requirement: Requirement) => void;
  onDelete: (requirement: Requirement) => void;
  onApprove: (requirement: Requirement) => void;
  onLink: (requirement: Requirement) => void;
}

export function RequirementsTable({
  requirements,
  isLoading,
  filters,
  onFiltersChange,
  onView,
  onEdit,
  onDelete,
  onApprove,
  onLink,
}: RequirementsTableProps) {
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [showFilters, setShowFilters] = useState(false);

  const toggleRow = (id: string) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedRows(newExpanded);
  };

  const handleFilterChange = (key: keyof RequirementFilters, value: string) => {
    onFiltersChange({
      ...filters,
      [key]: value === 'all' ? undefined : value,
    });
  };

  if (isLoading) {
    return (
      <div className="space-y-2">
        {[...Array(5)].map((_, i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Barra de busqueda y filtros */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por ID, titulo o descripcion..."
            value={filters.search || ''}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            className="pl-9"
          />
        </div>
        <Button
          variant={showFilters ? 'secondary' : 'outline'}
          size="icon"
          onClick={() => setShowFilters(!showFilters)}
        >
          <Filter className="h-4 w-4" />
        </Button>
      </div>

      {/* Filtros expandibles */}
      {showFilters && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 p-3 bg-muted rounded-lg">
          <Select
            value={filters.category || 'all'}
            onValueChange={(v) => handleFilterChange('category', v)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Categoria" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas las categorias</SelectItem>
              {Object.entries(categoryLabels).map(([value, label]) => (
                <SelectItem key={value} value={value}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={filters.level || 'all'}
            onValueChange={(v) => handleFilterChange('level', v)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Nivel" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los niveles</SelectItem>
              {Object.entries(levelLabels).map(([value, label]) => (
                <SelectItem key={value} value={value}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={filters.status || 'all'}
            onValueChange={(v) => handleFilterChange('status', v)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Estado" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los estados</SelectItem>
              {Object.entries(statusLabels).map(([value, label]) => (
                <SelectItem key={value} value={value}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={filters.priority || 'all'}
            onValueChange={(v) => handleFilterChange('priority', v)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Prioridad" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas las prioridades</SelectItem>
              {Object.entries(priorityLabels).map(([value, label]) => (
                <SelectItem key={value} value={value}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Button
            variant="ghost"
            size="sm"
            onClick={() =>
              onFiltersChange({
                search: '',
                category: 'all',
                level: 'all',
                status: 'all',
                priority: 'all',
              })
            }
          >
            Limpiar filtros
          </Button>
        </div>
      )}

      {/* Tabla */}
      <div className="border rounded-lg">
        <ScrollArea className="h-[500px]">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[40px]"></TableHead>
                <TableHead className="w-[120px]">ID</TableHead>
                <TableHead>Titulo</TableHead>
                <TableHead className="w-[100px]">Categoria</TableHead>
                <TableHead className="w-[80px]">Nivel</TableHead>
                <TableHead className="w-[100px]">Estado</TableHead>
                <TableHead className="w-[80px]">Prioridad</TableHead>
                <TableHead className="w-[60px]">Links</TableHead>
                <TableHead className="w-[60px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {requirements.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                    <FileText className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p>No hay requisitos que mostrar</p>
                    <p className="text-sm">Cree un nuevo requisito para comenzar</p>
                  </TableCell>
                </TableRow>
              ) : (
                requirements.map((req) => (
                  <>
                    <TableRow
                      key={req.id}
                      className={cn(
                        'cursor-pointer hover:bg-muted/50',
                        expandedRows.has(req.id) && 'bg-muted/30'
                      )}
                      onClick={() => toggleRow(req.id)}
                    >
                      <TableCell>
                        {expandedRows.has(req.id) ? (
                          <ChevronDown className="h-4 w-4" />
                        ) : (
                          <ChevronRight className="h-4 w-4" />
                        )}
                      </TableCell>
                      <TableCell className="font-mono text-xs">
                        {req.requirement_id}
                      </TableCell>
                      <TableCell>
                        <div className="max-w-[300px]">
                          <p className="font-medium truncate">{req.title}</p>
                          {req.parent_requirement_code && (
                            <p className="text-xs text-muted-foreground">
                              ← {req.parent_requirement_code}
                            </p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={cn('text-xs', categoryColors[req.category])}>
                          {categoryLabels[req.category]}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={cn('text-xs', levelColors[req.level])}>
                          {req.level.toUpperCase()}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={cn('text-xs', statusColors[req.status])}>
                          {statusLabels[req.status]}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={cn('text-xs', priorityColors[req.priority])}>
                          {priorityLabels[req.priority]}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                          <Link2 className="h-3 w-3" />
                          {(req.outgoing_links_count || 0) + (req.incoming_links_count || 0)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => onView(req)}>
                              <Eye className="h-4 w-4 mr-2" />
                              Ver detalle
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => onEdit(req)}>
                              <Edit className="h-4 w-4 mr-2" />
                              Editar
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => onLink(req)}>
                              <Link2 className="h-4 w-4 mr-2" />
                              Gestionar enlaces
                            </DropdownMenuItem>
                            {(req.status === 'draft' || req.status === 'proposed') && (
                              <DropdownMenuItem onClick={() => onApprove(req)}>
                                <CheckCircle className="h-4 w-4 mr-2" />
                                Aprobar
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => onDelete(req)}
                              className="text-red-600"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Eliminar
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                    {/* Fila expandida con detalles */}
                    {expandedRows.has(req.id) && (
                      <TableRow className="bg-muted/20">
                        <TableCell colSpan={9} className="p-4">
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <p className="text-muted-foreground mb-1">Descripcion:</p>
                              <p className="whitespace-pre-wrap">{req.description}</p>
                            </div>
                            <div className="space-y-2">
                              {req.acceptance_criteria && (
                                <div>
                                  <p className="text-muted-foreground">Criterios de Aceptacion:</p>
                                  <p>{req.acceptance_criteria}</p>
                                </div>
                              )}
                              <div className="grid grid-cols-2 gap-2 text-xs">
                                <div>
                                  <span className="text-muted-foreground">Creado:</span>{' '}
                                  {format(new Date(req.created_at), 'dd/MM/yyyy', { locale: es })}
                                </div>
                                {req.assigned_to_name && (
                                  <div>
                                    <span className="text-muted-foreground">Asignado:</span>{' '}
                                    {req.assigned_to_name}
                                  </div>
                                )}
                                {req.verification_method && (
                                  <div>
                                    <span className="text-muted-foreground">Verificacion:</span>{' '}
                                    {req.verification_method}
                                  </div>
                                )}
                                {req.compliance_standards.length > 0 && (
                                  <div className="col-span-2">
                                    <span className="text-muted-foreground">Estandares:</span>{' '}
                                    {req.compliance_standards.join(', ')}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </>
                ))
              )}
            </TableBody>
          </Table>
        </ScrollArea>
      </div>

      {/* Resumen */}
      <div className="flex items-center justify-between text-sm text-muted-foreground">
        <span>{requirements.length} requisitos encontrados</span>
        <div className="flex items-center gap-4">
          <span className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-full bg-red-500" />
            {requirements.filter((r) => r.priority === 'critical').length} criticos
          </span>
          <span className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-full bg-yellow-500" />
            {requirements.filter((r) => r.status === 'draft').length} borradores
          </span>
          <span className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-full bg-green-500" />
            {requirements.filter((r) => r.status === 'verified').length} verificados
          </span>
        </div>
      </div>
    </div>
  );
}
